// Calendar sync helpers.
//
// iOS quirk: When MedEasy runs as a PWA (added to home screen), tapping a
// link to a text/calendar URL just opens the in-app WebView preview, which
// can't render the .ics — the user sees a blank "Preview" page and never
// gets a Calendar import sheet. Safari proper handles it, but the WebView
// in standalone mode does NOT.
//
// Fix: fetch the .ics as a file and use the Web Share API to hand it to the
// OS. iOS shows its native share sheet with "Add to Calendar" as one of the
// share targets. This works in PWA mode, Safari, and Android Chrome alike.
// Desktop browsers (which don't implement file-share) fall back to a normal
// download via a hidden anchor — the user gets the .ics as a file they can
// double-click into Outlook / Apple Calendar / Google Calendar.

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function tripCalendarUrl(tripId) {
  return `${API_BASE}/api/trips/${tripId}/ics`;
}

/**
 * Email a single trip's .ics to the authenticated user. Apple Mail on iOS
 * is the most reliable iOS path — the attachment renders with a built-in
 * "Add to Calendar" button. No PWA WebView, no share sheet, no Files.
 * Returns true on success.
 */
export async function emailTripInvite(tripId) {
  if (!tripId) return false;
  try {
    const res = await fetch(`${API_BASE}/api/trips/${tripId}/email-invite`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `HTTP ${res.status}`);
    }
    return true;
  } catch (e) {
    console.error("emailTripInvite failed", e);
    return false;
  }
}

async function fetchIcsFile(tripId) {
  const res = await fetch(tripCalendarUrl(tripId), {
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`ics fetch failed: ${res.status}`);
  const text = await res.text();
  const filename = (() => {
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="?([^";]+)"?/i);
    return m ? m[1] : `medeasy-${tripId}.ics`;
  })();
  return new File([text], filename, { type: "text/calendar" });
}

function downloadFile(file) {
  // Desktop fallback — anchor download triggers Outlook/Apple/Google
  // Calendar's .ics handler in the OS.
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 250);
}

/**
 * Open the iOS/Android share sheet with a .ics for the given trip.
 * Returns the path taken: "share" | "download" | "error".
 */
export async function shareTripCalendar(tripId) {
  if (!tripId) return "error";
  try {
    const file = await fetchIcsFile(tripId);
    // Prefer Web Share API on iOS (and Android Chrome). The user picks
    // "Add to Calendar" in the system share sheet — this works in PWA
    // standalone mode where direct .ics URL navigation does NOT.
    if (typeof navigator !== "undefined" &&
        navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: "Medical appointment",
        text: "Add this appointment to your calendar — with a 2-day reminder.",
      });
      return "share";
    }
    // Desktop fallback — download triggers the OS calendar handler.
    downloadFile(file);
    return "download";
  } catch (e) {
    // User cancelled the share sheet — totally fine, no error.
    if (e && e.name === "AbortError") return "share";
    console.error("shareTripCalendar failed", e);
    return "error";
  }
}
